<script setup lang="ts">
import { Checkbox } from '@proj-airi/ui'
import { computed, onMounted, ref } from 'vue'

import Callout from '../../../layouts/Callout.vue'
import Button from '../../../misc/Button.vue'

import { useBackgroundActivityStore } from '../../../../stores/backgroundActivity'
import { useChatStore } from '../../../../stores/chat'
import { useContactsStore } from '../../../../stores/contacts'
import { useMemoryStore } from '../../../../stores/memory'
import { useAiriCardStore } from '../../../../stores/modules/airi-card'
import { useOfflineSyncStore } from '../../../../stores/offlineSync'
import { useWorldBookStore } from '../../../../stores/worldbook'

const backgroundActivityStore = useBackgroundActivityStore()

const saving = ref(false)
const saveResult = ref<'idle' | 'success' | 'error'>('idle')

// 设置页可能在后台系统初始化完成前打开；开关已开启但倒计时丢失时立即自愈。
onMounted(() => {
  if (backgroundActivityStore.enabled && !backgroundActivityStore.isCountdownRunning)
    backgroundActivityStore.start()
})

async function handleSave() {
  saving.value = true
  saveResult.value = 'idle'
  try {
    const offlineSyncStore = useOfflineSyncStore()
    if (!offlineSyncStore.isConfigured) {
      console.warn('离线同步未配置，跳过保存')
      saveResult.value = 'error'
      return
    }

    const contactsStore = useContactsStore()
    const chatStore = useChatStore()
    const cardStore = useAiriCardStore()
    const bgStore = backgroundActivityStore
    const worldBookStore = useWorldBookStore()
    const memoryStore = useMemoryStore()

    const providerCfg = bgStore.sendOptions?.providerConfig as Record<string, any> | undefined
    const apiKey = (providerCfg?.apiKey as string) || ''
    const baseUrl = (providerCfg?.baseUrl as string) || ''
    const modelId = bgStore.sendOptions?.model || ''

    const configs = contactsStore.contactsList
      .filter(c => c.id && c.name && !c.isGroup)
      .map((contact) => {
        const card = contact.cardId ? cardStore.getCard(contact.cardId) : undefined
        const systemPrompt = card
          ? [card.systemPrompt, card.description, card.personality].filter(Boolean).join('\n')
          : ''
        const allMessages = chatStore.getMessagesForContact(contact.id)
        const recentMessages = allMessages
          .filter((m: any) => m.role === 'user' || m.role === 'assistant')
          .slice(-50)
          .map((m: any) => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' }))

        let worldBook: string | undefined
        if (contact.worldBookIds && contact.worldBookIds.length > 0) {
          const entries = contact.worldBookIds
            .map(id => worldBookStore.getWorldBook(id))
            .filter(Boolean)
            .map(book => ({ keywords: book!.keywords, content: book!.content }))
          if (entries.length > 0)
            worldBook = JSON.stringify(entries)
        }

        const contactSummaries = memoryStore.summaries
          .filter(s => s.contactId === contact.id)
          .slice(-3)
          .map(s => s.content || s.summary || '')
          .filter(Boolean)
          .join('\n')

        return {
          contactId: contact.id,
          apiKey,
          baseUrl: baseUrl || undefined,
          modelId: modelId || undefined,
          systemPrompt,
          characterName: contact.name || '角色',
          characterAvatar: contact.avatar || undefined,
          recentMessages: JSON.stringify(recentMessages),
          worldBook,
          memorySummary: contactSummaries || undefined,
          minHours: bgStore.minHours,
          maxHours: bgStore.maxHours,
          quietTimeStart: bgStore.quietTimeStart,
          quietTimeEnd: bgStore.quietTimeEnd,
          timezoneOffset: new Date().getTimezoneOffset(),
          enabled: bgStore.enabled && !contactsStore.isFullyBlocked(contact.id),
          backgroundMessageEnabled: contact.backgroundMessageEnabled !== false,
          isGroup: contact.isGroup === true,
        }
      })

    if (configs.length > 0) {
      const synced = await offlineSyncStore.syncConfigs(configs)
      console.info(`📡 保存配置成功，同步了 ${synced} 个联系人`)
    }
    saveResult.value = 'success'
  }
  catch (err) {
    console.error('保存配置失败:', err)
    saveResult.value = 'error'
  }
  finally {
    saving.value = false
    setTimeout(() => saveResult.value = 'idle', 2000)
  }
}

// 倒计时状态显示
const countdownStatus = computed(() => {
  if (!backgroundActivityStore.enabled) {
    return '已禁用'
  }
  if (backgroundActivityStore.isCountdownRunning) {
    return '运行中'
  }
  return '已停止'
})

const statusColor = computed(() => {
  if (!backgroundActivityStore.enabled)
    return 'text-neutral-400'
  if (backgroundActivityStore.isCountdownRunning)
    return 'text-green-500'
  return 'text-yellow-500'
})

// 快捷预设
const presets = [
  {
    name: '默认',
    icon: 'i-solar:target-bold-duotone',
    minHours: 1,
    maxHours: 3,
    quietStart: '22:00',
    quietEnd: '08:00',
    description: '适合日常使用',
  },
  {
    name: '温和',
    icon: 'i-solar:clock-circle-bold-duotone',
    minHours: 3,
    maxHours: 6,
    quietStart: '23:00',
    quietEnd: '07:00',
    description: '不想被频繁打扰',
  },
  {
    name: '频繁',
    icon: 'i-solar:fire-bold-duotone',
    minHours: 0.5,
    maxHours: 1.5,
    quietStart: '22:00',
    quietEnd: '08:00',
    description: '想要更多互动',
  },
  {
    name: '低频',
    icon: 'i-solar:moon-bold-duotone',
    minHours: 6,
    maxHours: 12,
    quietStart: '00:00',
    quietEnd: '06:00',
    description: '偶尔联系即可',
  },
]

function applyPreset(preset: typeof presets[0]) {
  backgroundActivityStore.minHours = preset.minHours
  backgroundActivityStore.maxHours = preset.maxHours
  backgroundActivityStore.quietTimeStart = preset.quietStart
  backgroundActivityStore.quietTimeEnd = preset.quietEnd
}
</script>

<template>
  <div flex="~ col gap-4" p-2>
    <!-- 说明文档 -->
    <Callout label="主动消息策略">
      <p>
        配置 AI 如何主动联系你。
      </p>
      <div mt-3>
        <p mb-2 flex items-center gap-2>
          <span i-solar:clock-circle-bold-duotone text-blue-500 />
          倒计时机制
        </p>
        <ul ml-4 text-sm space-y-1>
          <li>• 用于 AI 主动发送消息</li>
          <li>• 在设定时间范围内随机触发</li>
          <li>• 支持安静时间（避免深夜打扰）</li>
          <li>• 用户回复时自动重置</li>
        </ul>
      </div>
    </Callout>

    <!-- 状态显示 -->
    <div
      bg="neutral-50 dark:neutral-800"
      rounded-lg p-4
    >
      <div flex items-center justify-between>
        <span>当前状态</span>
        <span :class="statusColor">
          {{ countdownStatus }}
        </span>
      </div>
    </div>

    <!-- 启用开关 -->
    <div
      flex items-start justify-between rounded-lg px-4 py-3
      bg="neutral-50 dark:neutral-800"
    >
      <div>
        <div text-sm>
          启用后台活动
        </div>
        <div text="xs neutral-500" mt-0.5>
          允许 AI 在一段时间后主动联系你，若开启会提高部分后台 token 消耗
        </div>
      </div>
      <Checkbox v-model="backgroundActivityStore.enabled" />
    </div>

    <!-- 倒计时配置 -->
    <div
      bg="neutral-50 dark:neutral-800"
      rounded-lg p-4
    >
      <div mb-3 flex items-center gap-2>
        <div i-solar:clock-circle-bold-duotone text-xl text-blue-500 />
        <h3>
          倒计时范围
        </h3>
      </div>
      <p text="sm neutral-500" mb-3>
        AI 会在这个时间范围内随机选择一个时间点主动联系你
      </p>
      <div grid="~ cols-2 gap-3">
        <div>
          <label text="sm neutral-600 dark:neutral-400" mb-2 block>最小时间（小时）</label>
          <input
            v-model.number="backgroundActivityStore.minHours"
            type="number"
            min="0"
            max="24"
            step="0.1"
            class="w-full border border-neutral-300 rounded-lg bg-white px-3 py-2 text-sm outline-none transition-colors dark:border-neutral-600 focus:border-primary-500 dark:bg-neutral-900 dark:focus:border-primary-400"
            placeholder="1.0"
          >
        </div>
        <div>
          <label text="sm neutral-600 dark:neutral-400" mb-2 block>最大时间（小时）</label>
          <input
            v-model.number="backgroundActivityStore.maxHours"
            type="number"
            min="0"
            max="24"
            step="0.1"
            class="w-full border border-neutral-300 rounded-lg bg-white px-3 py-2 text-sm outline-none transition-colors dark:border-neutral-600 focus:border-primary-500 dark:bg-neutral-900 dark:focus:border-primary-400"
            placeholder="3.0"
          >
        </div>
      </div>
      <div text="xs neutral-500" mt-2 flex items-center gap-1>
        <span i-solar:lightbulb-bolt-bold-duotone text-amber-500 />
        范围：0-24小时，0表示立即触发
      </div>
      <div
        v-if="backgroundActivityStore.minHours >= backgroundActivityStore.maxHours"
        bg="red-50 dark:red-950" text="red-600 dark:red-400 sm" mt-3 flex items-center gap-1 rounded-lg p-2
      >
        <span i-solar:danger-triangle-bold-duotone />
        最小时间不能大于或等于最大时间
      </div>
    </div>

    <!-- 安静时间配置 -->
    <div
      bg="neutral-50 dark:neutral-800"
      rounded-lg p-4
    >
      <div mb-3 flex items-center gap-2>
        <div i-solar:moon-sleep-bold-duotone text-xl text-indigo-500 />
        <h3>
          安静时间
        </h3>
      </div>
      <p text="sm neutral-500" mb-3>
        在这个时间段内，即使倒计时结束也不会主动联系（避免深夜打扰）
      </p>
      <div grid="~ cols-2 gap-3">
        <div min-w-0>
          <label text="sm neutral-600 dark:neutral-400" mb-2 block>开始时间</label>
          <input
            v-model="backgroundActivityStore.quietTimeStart"
            type="time"
            class="w-full border border-neutral-300 rounded-lg bg-white px-2 py-2 text-sm outline-none transition-colors dark:border-neutral-600 focus:border-primary-500 dark:bg-neutral-900 dark:focus:border-primary-400"
          >
        </div>
        <div min-w-0>
          <label text="sm neutral-600 dark:neutral-400" mb-2 block>结束时间</label>
          <input
            v-model="backgroundActivityStore.quietTimeEnd"
            type="time"
            class="w-full border border-neutral-300 rounded-lg bg-white px-2 py-2 text-sm outline-none transition-colors dark:border-neutral-600 focus:border-primary-500 dark:bg-neutral-900 dark:focus:border-primary-400"
          >
        </div>
      </div>
      <div text="xs neutral-500" mt-2 flex items-center gap-1>
        <span i-solar:lightbulb-bolt-bold-duotone text-amber-500 />
        支持跨天设置，例如 22:00 - 08:00
      </div>
    </div>

    <!-- 快捷预设 -->
    <div
      bg="neutral-50 dark:neutral-800"
      rounded-lg p-4
    >
      <div mb-3 flex items-center gap-2>
        <div i-solar:widget-5-bold-duotone text-xl text-amber-500 />
        <h3>
          快捷预设
        </h3>
      </div>
      <div grid="~ cols-2 gap-3">
        <button
          v-for="preset in presets"
          :key="preset.name"
          class="group flex flex-col items-start border border-neutral-300 rounded-lg bg-white p-3 text-left transition-all dark:border-neutral-600 hover:border-primary-500 dark:bg-neutral-900 hover:shadow-sm dark:hover:border-primary-400"
          @click="applyPreset(preset)"
        >
          <div mb-1 flex items-center gap-2>
            <span :class="preset.icon" text-xl />
            <span text-sm>{{ preset.name }}</span>
          </div>
          <span text="xs neutral-500">{{ preset.description }}</span>
          <div text="xs neutral-400" mt-1>
            {{ preset.minHours }}-{{ preset.maxHours }}h
          </div>
        </button>
      </div>
    </div>

    <!-- 保存配置 -->
    <Button
      :disabled="saving"
      @click="handleSave"
    >
      <div :class="saveResult === 'success' ? 'i-solar:check-circle-bold-duotone' : saveResult === 'error' ? 'i-solar:close-circle-bold-duotone' : 'i-solar:cloud-upload-bold-duotone'" mr-2 />
      {{ saving ? '保存中...' : saveResult === 'success' ? '已保存' : saveResult === 'error' ? '保存失败' : '保存配置到服务器' }}
    </Button>

    <!-- 操作按钮 -->
    <div grid="~ cols-2 gap-3">
      <Button
        :disabled="!backgroundActivityStore.enabled"
        @click="backgroundActivityStore.startCountdown()"
      >
        <div i-solar:refresh-bold-duotone mr-2 />
        重新开始倒计时
      </Button>
      <Button
        variant="secondary"
        @click="backgroundActivityStore.manualTrigger()"
      >
        <div i-solar:test-tube-bold-duotone mr-2 />
        手动触发一次
      </Button>
    </div>
  </div>
</template>
